/**
 * Parse an ISO date string from the API as UTC when no timezone is present.
 * Backend sends datetimes without "Z" (e.g. "2026-03-11T19:21:46.591570"); in JS those are
 * parsed as local time, which makes "X ago" wrong. This treats them as UTC so the instant
 * is correct and relative time displays correctly in the user's timezone.
 */
export function parseUtcISO(value: string | null | undefined): Date | undefined {
    if (value == null || value === '') return undefined;
    const s = value.trim();
    if (!s) return undefined;
    // Already has timezone (Z or ±HH:MM / ±HHMM)
    if (/Z$|[-+]\d{2}:?\d{2}$/.test(s)) return new Date(s);
    return new Date(s + 'Z');
}

/**
 * Formatta una data nel formato "Tue, 25 Jun" (giorno abbreviato, data, mese abbreviato)
 * Se includeYear è true, aggiunge l'anno: "Tue, 25 Jun 2024"
 */
const isValidDate = (date: Date | null | undefined): date is Date => {
    return !!date && !Number.isNaN(date.getTime());
};

export const formatShortDate = (date: Date | null | undefined, includeYear: boolean = false): string => {
    if (!isValidDate(date)) return '-';

    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    const day = days[date.getDay()];
    const dayNum = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    
    if (includeYear) {
        return `${day}, ${dayNum} ${month} ${year}`;
    }
    return `${day}, ${dayNum} ${month}`;
};

/**
 * Formatta un timestamp relativo (es. "5h ago", "2d ago", "30m ago")
 */
export const formatRelativeTime = (date: Date | null | undefined): string => {
    if (!isValidDate(date)) return '-';

    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) {
        return 'just now';
    } else if (diffMins < 60) {
        return `${diffMins}m ago`;
    } else if (diffHours < 24) {
        return `${diffHours}h ago`;
    } else if (diffDays < 7) {
        return `${diffDays}d ago`;
    } else {
        return formatShortDate(date);
    }
};

